import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createAttachStream } from '../utils/callMediaAttachUtils';
import { getVideoPlaybackStream } from '../utils/callFeedUtils';

/**
 * Binds local/remote streams to media elements with generation bumps on track churn
 * (camera flip, mute/unmute without stream replacement).
 */
export function useCallMediaAttachment({
  localVideoRef,
  remoteVideoRef,
  remoteAudioRef,
  localStream,
  remoteVideoStream,
  remoteAudioStream,
  callEnded,
}) {
  const mediaElementTrackSignatureRef = useRef(new WeakMap());
  const [mediaAttachGeneration, setMediaAttachGeneration] = useState(0);

  const bumpMediaAttachGeneration = useCallback(() => {
    setMediaAttachGeneration((n) => n + 1);
  }, []);

  const attachStream = useMemo(
    () => createAttachStream(mediaElementTrackSignatureRef),
    []
  );

  // Matrix SDK may swap cameras without replacing the MediaStream object; bump attach when tracks change.
  useEffect(() => {
    if (!localStream) return undefined;
    const bump = () => bumpMediaAttachGeneration();
    localStream.addEventListener('addtrack', bump);
    localStream.addEventListener('removetrack', bump);
    return () => {
      localStream.removeEventListener('addtrack', bump);
      localStream.removeEventListener('removetrack', bump);
    };
  }, [localStream, bumpMediaAttachGeneration]);

  useEffect(() => {
    if (!remoteVideoStream) return undefined;
    const bump = () => bumpMediaAttachGeneration();
    remoteVideoStream.addEventListener('addtrack', bump);
    remoteVideoStream.addEventListener('removetrack', bump);
    return () => {
      remoteVideoStream.removeEventListener('addtrack', bump);
      remoteVideoStream.removeEventListener('removetrack', bump);
    };
  }, [remoteVideoStream, bumpMediaAttachGeneration]);

  useEffect(() => {
    if (!remoteVideoStream) return undefined;
    const bump = () => bumpMediaAttachGeneration();
    const tracks = remoteVideoStream.getVideoTracks?.() || [];
    tracks.forEach((t) => {
      t.addEventListener('mute', bump);
      t.addEventListener('unmute', bump);
      t.addEventListener('ended', bump);
    });
    return () => {
      tracks.forEach((t) => {
        t.removeEventListener('mute', bump);
        t.removeEventListener('unmute', bump);
        t.removeEventListener('ended', bump);
      });
    };
  }, [remoteVideoStream, bumpMediaAttachGeneration]);

  useEffect(() => {
    if (!remoteAudioStream) return undefined;
    const bump = () => bumpMediaAttachGeneration();
    remoteAudioStream.addEventListener('addtrack', bump);
    remoteAudioStream.addEventListener('removetrack', bump);
    return () => {
      remoteAudioStream.removeEventListener('addtrack', bump);
      remoteAudioStream.removeEventListener('removetrack', bump);
    };
  }, [remoteAudioStream, bumpMediaAttachGeneration]);

  useEffect(() => {
    if (!localStream) return undefined;
    const bump = () => bumpMediaAttachGeneration();
    const tracks = localStream.getVideoTracks?.() || [];
    tracks.forEach((t) => {
      t.addEventListener('mute', bump);
      t.addEventListener('unmute', bump);
      t.addEventListener('ended', bump);
    });
    return () => {
      tracks.forEach((t) => {
        t.removeEventListener('mute', bump);
        t.removeEventListener('unmute', bump);
        t.removeEventListener('ended', bump);
      });
    };
  }, [localStream, bumpMediaAttachGeneration]);

  useEffect(() => {
    if (!remoteAudioStream) return undefined;
    const bump = () => bumpMediaAttachGeneration();
    const tracks = remoteAudioStream.getAudioTracks?.() || [];
    tracks.forEach((t) => {
      t.addEventListener('mute', bump);
      t.addEventListener('unmute', bump);
      t.addEventListener('ended', bump);
    });
    return () => {
      tracks.forEach((t) => {
        t.removeEventListener('mute', bump);
        t.removeEventListener('unmute', bump);
        t.removeEventListener('ended', bump);
      });
    };
  }, [remoteAudioStream, bumpMediaAttachGeneration]);

  useLayoutEffect(() => {
    attachStream(localVideoRef.current, getVideoPlaybackStream(localStream), callEnded, ['video']);
    attachStream(remoteVideoRef.current, getVideoPlaybackStream(remoteVideoStream), callEnded, [
      'video',
    ]);
    attachStream(remoteAudioRef.current, remoteAudioStream, callEnded, ['audio']);
  }, [
    localStream,
    remoteVideoStream,
    remoteAudioStream,
    attachStream,
    callEnded,
    mediaAttachGeneration,
    localVideoRef,
    remoteVideoRef,
    remoteAudioRef,
  ]);

  return {
    bumpMediaAttachGeneration,
  };
}
